import { mulberry32 } from '@/lib/math'

/** CSS size of one tile. The bitmap matches device pixels (up to 2×) so the grain stays fine. */
export const GRAIN_TILE_CSS_PX = 128

/** Fixed seed: the texture is identical on every visit (and in screenshots). */
const SEED = 0x5eed_9a1e

/**
 * Strongest darkening of a single grain (≈ 1.6%). The average is about a third of that,
 * so on pearl (#fafafc) the page moves by ~1–2 levels: felt as texture, never as tint.
 */
const PEAK_ALPHA = 0.016

let cached: string | null | undefined

/**
 * Builds the grain tile once and caches it: fine, uniform, per-pixel noise as cool slate
 * pixels with varying alpha (no low-frequency mottling, so nothing reads as blotches).
 * Normal alpha blending of a dark pixel with alpha `a·n` equals multiplying by `1 − a·n`,
 * so this looks like a multiply grain without paying for `mix-blend-mode` on a
 * full-screen layer.
 *
 * @returns A PNG data URL, or `null` when canvas 2D is unavailable.
 */
export function getGrainTile(): string | null {
  if (cached !== undefined) return cached
  cached = null
  if (typeof document === 'undefined') return cached

  const scale = Math.min(2, Math.max(1, Math.round(window.devicePixelRatio || 1)))
  const size = GRAIN_TILE_CSS_PX * scale
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')
  if (!context) return cached

  const random = mulberry32(SEED)
  const image = context.createImageData(size, size)
  const data = image.data
  for (let offset = 0; offset < data.length; offset += 4) {
    // Two samples: a gentle triangular distribution, so few grains sit at either extreme.
    const grain = (random() + random()) / 2
    // Slate (#0f1222), never black or brown: the grain stays cool and neutral on pearl.
    data[offset] = 15
    data[offset + 1] = 18
    data[offset + 2] = 34
    data[offset + 3] = Math.round(PEAK_ALPHA * grain * grain * 255)
  }
  context.putImageData(image, 0, 0)

  try {
    cached = canvas.toDataURL('image/png')
  } catch {
    cached = null
  }
  return cached
}
